import { ReactNode, useState } from 'react';
import { format } from 'date-fns';
import { AcademicCapIcon, DocumentTextIcon, FolderIcon, PlayIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import AttachmentChips from '../../components/resources/AttachmentChips';
import { Course } from '../../types/elearning';
import { AppEvent } from '../../types/event';
import { Hub } from '../../types/hub';
import { Program, ProgramHub } from '../../types/program';
import { WPMedia, WPPost } from '../../types/wordpress';
import { decodeHtmlEntities, stripHtml } from '../../utils/htmlEntities';
import { isUnderRevision } from '../../utils/resourceRevision';
import { getAttachmentFormat } from '../../utils/resourceAttachments';
import { CalendarCorner, CalendarMark, ProposalCard, SaveMark, ViewCourseMark, WatchMark } from './optionBits';
import { ReviewFrame, columnCard } from './reviewUi';

function Plate({ icon }: { icon: ReactNode }) {
  return <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">{icon}</div>;
}

function Door({ title }: { title: string }) {
  return <span className="flex items-center min-h-[44px] text-sm text-primary">{title}</span>;
}

function Doors({ hubs }: { hubs: ProgramHub[] }) {
  return (
    <div className="mt-4 border-t border-gray-200 pt-2 flex flex-col">
      {hubs.map((hub) => (
        <Door key={hub.id} title={hub.title} />
      ))}
    </div>
  );
}

function hubWord(count: number) {
  return count === 1 ? '1 hub' : `${count} hubs`;
}

function resourceWord(count: number) {
  return count === 1 ? '1 resource' : `${count} resources`;
}

function sectionWord(count: number) {
  return count === 1 ? '1 section' : `${count} sections`;
}

function fullness(hub: Hub) {
  const resources = hub.resources.length;
  const sections = hub.sections?.length ?? 0;
  return sections > 0 ? `${resourceWord(resources)} · ${sectionWord(sections)}` : resourceWord(resources);
}

function Kind({ children }: { children: string }) {
  return <p className="text-sm font-semibold text-primary">{children}</p>;
}

function postTitle(post: WPPost) {
  return decodeHtmlEntities(post.title.rendered);
}

function postFacts(post: WPPost, attachments: WPMedia[]) {
  const files = attachments.length === 1 ? '1 file' : `${attachments.length} files`;
  return {
    title: postTitle(post),
    excerpt: stripHtml(post.excerpt.rendered),
    date: format(new Date(post.date), 'MMMM d, yyyy'),
    under: isUnderRevision(post.acf?.under_revision),
    files,
  };
}

function resourceOffer(attachments: WPMedia[]) {
  if (attachments.length === 0) return '';
  if (attachments.length === 1) return getAttachmentFormat(attachments[0]).label;
  return `${attachments.length} files`;
}

function namesCoverage(sentence: string) {
  const standard = /\b(protocols?|standards?|recommendations?|guidelines?|regulations?)\b/i.test(sentence);
  const covers = /\b(hospitals?|patients?|ontario|clinicians?|providers?|populations?|staff)\b/i.test(sentence);
  return standard && covers;
}

function authorListOnly(text: string) {
  return !text.split(/\s+/).some((word) => /^[a-z]{4,}/.test(word.replace(/[^a-z]/gi, '')));
}

function resourceClinical(excerpt: string) {
  const text = plain(excerpt);
  if (!text) return '';
  if (/\b(pmid\b|doi\s*:|doi\.org)/i.test(text)) return '';
  const sentences = text.split(/(?<=[.!?])\s+/).map((sentence) => sentence.trim()).filter(Boolean);
  const hit = sentences.find(namesCoverage);
  if (hit) return hit;
  if (authorListOnly(text)) return '';
  const prose = sentences.filter((sentence) => !/^(written by|authors?:)\b/i.test(sentence));
  return prose[0] ?? '';
}

function Revision() {
  return <span className="text-sm font-medium text-warning">Under revision</span>;
}

function FactLine({ date, under, files }: { date: string; under: boolean; files?: string }) {
  return (
    <span className="text-sm text-gray-600">
      {date}
      {date && under && ' · '}
      {under && <Revision />}
      {files ? `${date || under ? ' · ' : ''}${files}` : ''}
    </span>
  );
}

function courseLength(course: Course) {
  const minutes = course.estimatedTime;
  const modules = course.modules?.length ?? 0;
  const time = minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h${minutes % 60 ? ` ${minutes % 60}m` : ''}`;
  const moduleLabel = modules === 1 ? '1 module' : `${modules} modules`;
  return { time, moduleLabel, line: modules > 0 ? `${time} · ${moduleLabel}` : time };
}

function startOf(event: AppEvent) {
  return new Date(event.startAt);
}

function clockLine(event: AppEvent) {
  const start = startOf(event);
  if (event.allDay) return 'All day';
  const end = event.endAt ? new Date(event.endAt) : null;
  return `${format(start, 'h:mm a')}${end ? ` – ${format(end, 'h:mm a')}` : ''}`;
}

function eventBlurb(excerpt: string) {
  let text = decodeHtmlEntities(plain(excerpt)).replace(/^@\s*/, '').replace(/^title:\s*/i, '');
  const cut = text.search(/\b(?:watch(?:\s+(?:archive|register))?|achieve|add to calendar|if you require a certificate|evaluation survey|you must register|https?:\/\/|www\.)/i);
  if (cut >= 0) text = text.slice(0, cut);
  text = text.replace(/\s+(?:watc\w*|achiev\w*|regist\w*|evaluat\w*)\s*$/i, '');
  text = text.replace(/\s+/g, ' ').trim();
  const split = text.match(/^(.*?)\s+presented\s+by\s*:?\s*(.*)$/i);
  if (!split) {
    if (!text || /^(watch|register|add to calendar|evaluation survey)$/i.test(text)) return { topic: '', presenter: '' };
    return { topic: text, presenter: '' };
  }
  return {
    topic: split[1].replace(/[\s:–—-]+$/, '').trim(),
    presenter: split[2].replace(/^[\s:]+/, '').replace(/[\s:–—-]+$/, '').trim(),
  };
}

function eventTitle(event: AppEvent) {
  const start = startOf(event);
  const forms = [format(start, 'MMMM d, yyyy'), format(start, 'MMMM d yyyy'), format(start, 'MMM d, yyyy'), format(start, 'MMMM do, yyyy')];
  let title = event.title.replace(/\s+/g, ' ').trim();
  for (const form of forms) {
    const escaped = form.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const trailing = new RegExp(`(?:\\s*[-–—]\\s*|\\s+)${escaped}\\.?\\s*$`, 'i');
    if (trailing.test(title)) {
      title = title.replace(trailing, '').replace(/[-–—]\s*$/, '').trim();
      break;
    }
  }
  return title;
}

function DayBadge({
  event,
  tone,
  size,
  dayClass,
}: {
  event: AppEvent;
  tone: 'primary' | 'neutral';
  size: 'lg' | 'md';
  dayClass: string;
}) {
  const box = size === 'lg' ? 'w-20 h-20' : 'w-12 h-12';
  return (
    <div className={`${box} rounded-lg flex flex-col items-center justify-center flex-shrink-0 ${tone === 'neutral' ? 'bg-gray-100' : 'bg-primary/10'}`}>
      <span className={`${dayClass} font-bold ${tone === 'neutral' ? 'text-gray-600' : 'text-primary'}`}>{format(startOf(event), 'd')}</span>
      {size === 'lg' && (
        <span className={`text-sm uppercase ${tone === 'neutral' ? 'text-gray-500' : 'text-gray-600'}`}>{format(startOf(event), 'MMM')}</span>
      )}
    </div>
  );
}

function plain(value?: string | null) {
  if (!value) return '';
  return stripHtml(value).replace(/\s+/g, ' ').trim();
}

function PurposeButton({ children }: { children: string }) {
  return (
    <button type="button" className="inline-flex items-center justify-center min-h-[44px] px-3 rounded-md bg-primary text-white text-sm font-medium flex-shrink-0">
      {children}
    </button>
  );
}

function CalendarDay({ event, past, large }: { event: AppEvent; past: boolean; large: boolean }) {
  const tone = past ? 'bg-gray-100 text-gray-600' : 'bg-primary/10 text-primary';
  return (
    <div className={`${large ? 'w-20 h-20' : 'w-16 h-16'} rounded-lg flex flex-col items-center justify-center flex-shrink-0 ${tone}`}>
      <span className={`${large ? 'text-2xl' : 'text-xl'} font-bold leading-none`}>{format(startOf(event), 'd')}</span>
      <span className="text-sm uppercase leading-none mt-1">{format(startOf(event), 'MMM')}</span>
    </div>
  );
}

function solidProgram(program: Program, optionId: string) {
  const count = hubWord(program.hubs.length);
  if (optionId === 'solid-row') {
    return (
      <div className="flex items-center gap-4 w-full min-h-[48px] px-4 py-3 bg-white border border-gray-200 rounded-lg">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-primary">Program · {count}</p>
          <p className="text-base font-semibold text-gray-900">{program.title}</p>
        </div>
        <PurposeButton>Enter</PurposeButton>
      </div>
    );
  }
  const shown = program.hubs.length > 4 ? program.hubs.slice(0, 3) : program.hubs;
  const rest = program.hubs.length - shown.length;
  const intro = plain(program.introduction);
  return (
    <ProposalCard className="p-4">
      <p className="text-sm font-semibold text-primary">Program · {count}</p>
      <h3 className="mt-1 text-lg font-semibold text-gray-900">{program.title}</h3>
      {intro && <p className="mt-2 text-sm text-gray-600 line-clamp-2">{intro}</p>}
      {shown.length > 0 && (
        <ol className="mt-4">
          {shown.map((hub, index) => (
            <li key={hub.id} className="flex gap-3">
              <div className="flex flex-col items-center">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-semibold flex items-center justify-center">
                  {index + 1}
                </span>
                {(index < shown.length - 1 || rest > 0) && <span className="w-px flex-1 bg-primary/30 my-1" />}
              </div>
              <span className="text-sm text-gray-900 pt-0.5 pb-3">{hub.title}</span>
            </li>
          ))}
          {rest > 0 && <li className="pl-9 text-sm text-gray-600">{rest === 1 ? '1 more hub on this path' : `${rest} more hubs on this path`}</li>}
        </ol>
      )}
      <div className="mt-2">
        <PurposeButton>Enter</PurposeButton>
      </div>
    </ProposalCard>
  );
}

function solidHub(hub: Hub, optionId: string, frame: ReviewFrame) {
  const count = resourceWord(hub.resources.length);
  const about = plain(hub.description);
  const horizontal = optionId === 'solid-row' || optionId === 'solid-chip';
  if (horizontal) {
    return (
      <div className="flex w-full overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-primary/10 text-primary px-3 py-3 flex flex-col items-center justify-center text-center min-w-[5.5rem]">
          <FolderIcon className="h-6 w-6" aria-hidden />
          <span className="mt-1 text-sm font-medium">{count}</span>
        </div>
        <div className="min-w-0 flex-1 px-4 py-3 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            {hub.program?.title && <p className="text-sm text-primary">{hub.program.title}</p>}
            <p className={`${optionId === 'solid-chip' ? 'text-sm' : 'text-base'} font-semibold text-gray-900`}>{hub.title}</p>
          </div>
          {optionId === 'solid-row' && <PurposeButton>Open</PurposeButton>}
          <SaveMark />
        </div>
      </div>
    );
  }
  const card = (
    <ProposalCard className="overflow-hidden">
      <div className="flex items-center gap-2 bg-primary/10 text-primary px-4 min-h-[44px]">
        <FolderIcon className="h-5 w-5 flex-shrink-0" aria-hidden />
        <span className="text-sm font-medium">{count}</span>
      </div>
      <div className="px-4 py-4">
        {hub.program?.title && <p className="text-sm text-primary">{hub.program.title}</p>}
        <p className="text-lg font-semibold text-gray-900">{hub.title}</p>
        {about && <p className="mt-2 text-sm text-gray-600 line-clamp-2">{about}</p>}
        <div className="mt-4 flex items-center gap-2">
          <PurposeButton>Open folder</PurposeButton>
          <SaveMark />
        </div>
      </div>
    </ProposalCard>
  );
  return optionId === 'solid-column' ? columnCard(frame, card) : card;
}

function solidResource(post: WPPost, attachments: WPMedia[], optionId: string, frame: ReviewFrame) {
  const { title, date, under, files, excerpt } = postFacts(post, attachments);
  const titleClass = optionId === 'solid-row' ? 'text-base' : 'text-lg';
  const card = (
    <ProposalCard className="overflow-hidden border-l-4 border-l-primary">
      <div className="px-4 py-4">
        <div className="flex items-start gap-3">
          <p className={`${titleClass} font-semibold text-gray-900 min-w-0 flex-1`}>{title}</p>
          <SaveMark />
        </div>
        {optionId !== 'solid-row' && excerpt && <p className="mt-2 text-sm text-gray-600 line-clamp-2">{excerpt}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            {under && <p className="text-sm font-medium text-warning">Under revision</p>}
            <p className="text-sm text-gray-600">
              {date} · {files}
            </p>
          </div>
          <PurposeButton>Open document</PurposeButton>
        </div>
      </div>
    </ProposalCard>
  );
  return optionId === 'solid-column' ? columnCard(frame, card) : card;
}

function solidEvent(event: AppEvent, optionId: string, frame: ReviewFrame) {
  const past = optionId === 'solid-past';
  const stacked = optionId === 'solid-column';
  const row = optionId === 'solid-row';
  const fact = past
    ? [format(startOf(event), 'MMMM d, yyyy'), event.venue].filter(Boolean).join(' · ')
    : [clockLine(event), event.venue].filter(Boolean).join(' · ');
  const day = <CalendarDay event={event} past={past} large={!row} />;
  const actions = (
    <div className="mt-3 flex flex-wrap gap-2">
      {past && event.recordingUrl && <WatchMark />}
      {!past && <PurposeButton>I&apos;ll attend</PurposeButton>}
      {!row && <CalendarMark />}
    </div>
  );
  if (stacked) {
    return columnCard(
      frame,
      <ProposalCard className="p-4">
        {day}
        <p className="mt-4 text-lg font-semibold text-gray-900">{event.title}</p>
        <p className="mt-1 text-sm text-gray-600">{fact}</p>
        {actions}
      </ProposalCard>,
    );
  }
  return (
    <div className={`w-full bg-white border border-gray-200 rounded-lg ${row ? 'px-4 py-3' : 'p-4 shadow-sm'}`}>
      <div className="flex items-start gap-4">
        {day}
        <div className="min-w-0 flex-1">
          <p className={`${row ? 'text-base' : 'text-lg'} font-semibold text-gray-900`}>{event.title}</p>
          <p className="mt-1 text-sm text-gray-600">{fact}</p>
          {actions}
        </div>
      </div>
    </div>
  );
}

function CourseTrack({ modules, finished = 0 }: { modules: number; finished?: number }) {
  const noun = modules === 1 ? 'module' : 'modules';
  const width = modules > 0 ? Math.round((finished / modules) * 100) : 0;
  return (
    <div className="mt-3">
      <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
        <div className="h-full bg-primary" style={{ width: `${width}%` }} />
      </div>
      <p className="mt-1 text-sm text-gray-600">{finished} of {modules} {noun}</p>
    </div>
  );
}

export type CourseStage = 'open' | 'active' | 'done';

function solidCourse(course: Course, optionId: string, frame: ReviewFrame, stage: CourseStage, finished: number) {
  const { line } = courseLength(course);
  const modules = course.modules?.length ?? 0;
  const band = stage === 'done' ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary';
  const label = stage === 'done' ? 'Completed' : stage === 'active' ? 'In progress' : 'Not enrolled';
  const action = <PurposeButton>{stage === 'done' ? 'Review course' : stage === 'active' ? 'Continue' : 'Start course'}</PurposeButton>;
  if (optionId === 'solid-column' || optionId === 'solid-side') {
    const card = (
      <ProposalCard className="overflow-hidden">
        <div className={`flex items-center gap-2 px-4 min-h-[44px] ${band}`}>
          <AcademicCapIcon className="h-5 w-5" aria-hidden />
          <span className="text-sm font-medium">{label}</span>
        </div>
        <div className="px-4 py-4">
          <p className="text-lg font-semibold text-gray-900">{course.title}</p>
          <p className="mt-2 text-sm text-gray-600">{line}</p>
          {stage !== 'done' && <CourseTrack modules={modules} finished={finished} />}
          <div className="mt-3">{action}</div>
        </div>
      </ProposalCard>
    );
    return optionId === 'solid-side' ? <div className="max-w-xs">{card}</div> : columnCard(frame, card);
  }
  return (
    <div className="flex w-full overflow-hidden bg-white border border-gray-200 rounded-lg">
      <div className={`flex items-center justify-center px-3 w-28 text-sm font-medium text-center ${band}`}>{label}</div>
      <div className="min-w-0 flex-1 px-4 py-3 flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-gray-900">{course.title}</p>
          <p className="mt-1 text-sm text-gray-600">{line}</p>
          {stage !== 'done' && <CourseTrack modules={modules} finished={finished} />}
        </div>
        {action}
      </div>
    </div>
  );
}


function QuietAction({ children, icon, label }: { children: string; icon?: ReactNode; label?: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      className="relative inline-flex items-center gap-2 text-sm font-medium text-primary leading-5 before:absolute before:inset-x-0 before:top-0 before:-bottom-6"
    >
      {icon}
      {children}
    </button>
  );
}

function ModernProgram({ program, optionId }: { program: Program; optionId: string }) {
  const [expanded, setExpanded] = useState(false);
  const count = hubWord(program.hubs.length);
  if (optionId === 'modern-row') {
    return (
      <div className="flex items-start gap-4 w-full p-4 bg-white border border-gray-200 rounded-lg">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-primary">Program</p>
          <p className="text-base font-semibold text-gray-900">{program.title}</p>
          <p className="text-sm text-gray-600">{count} inside</p>
        </div>
        <div className="flex flex-col items-end">
          <SaveMark align="end" />
          <QuietAction>Enter</QuietAction>
        </div>
      </div>
    );
  }
  const limit = 4;
  const hidden = Math.max(program.hubs.length - limit, 0);
  const shown = hidden > 0 && !expanded ? program.hubs.slice(0, limit) : program.hubs;
  const intro = plain(program.introduction);
  return (
    <ProposalCard className="p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-primary">Program</p>
          <h3 className="mt-1 text-lg font-semibold text-gray-900">{program.title}</h3>
        </div>
        <SaveMark align="end" />
      </div>
      {intro && <p className="mt-2 text-sm text-gray-600">{intro}</p>}
      <div className="mt-4">
        <p className="text-sm text-gray-600">{count} inside</p>
        <ul className="mt-2 border-t border-gray-200">
          {shown.map((hub) => (
            <li key={hub.id} className="border-b border-gray-200">
              <button type="button" className="flex items-center gap-3 w-full min-h-[44px] text-left">
                <span className="min-w-0 flex-1 text-sm font-medium text-gray-900">{hub.title}</span>
                <span className="text-sm text-gray-600 flex-shrink-0">{resourceWord(hub.resources.length)}</span>
              </button>
            </li>
          ))}
          {hidden > 0 && (
            <li className="border-b border-gray-200">
              <button type="button" className="min-h-[44px] text-sm font-medium text-primary" onClick={() => setExpanded((open) => !open)}>
                {expanded ? 'Show less' : `+ ${hidden} more`}
              </button>
            </li>
          )}
        </ul>
      </div>
      <div className="mt-4">
        <QuietAction>Enter</QuietAction>
      </div>
    </ProposalCard>
  );
}

function modernHub(hub: Hub, optionId: string, frame: ReviewFrame, place?: 'search') {
  const count = resourceWord(hub.resources.length);
  const about = plain(hub.description);
  const programLine = hub.program?.title ? `In ${hub.program.title}` : '';
  if (optionId === 'modern-row' && place === 'search') {
    return (
      <div className="overflow-x-auto">
        <div className="flex w-full min-w-[28rem] overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="bg-primary/10 text-primary w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem] shrink-0 px-2 py-3 flex flex-col items-center justify-center text-center">
            <FolderIcon className="h-5 w-5" aria-hidden />
            <span className="mt-1 text-sm font-medium">{count}</span>
          </div>
          <div className="min-w-0 flex-1 px-4 py-4 flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-primary">Hub</p>
              <p className="text-base font-semibold text-gray-900">{hub.title}</p>
              {programLine && <p className="mt-1 text-sm text-gray-600">{programLine}</p>}
            </div>
            <SaveMark align="end" />
          </div>
        </div>
      </div>
    );
  }
  if (optionId === 'modern-chip') {
    return (
      <div className="inline-flex flex-wrap items-center gap-3 max-w-full px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-primary/10 text-primary w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem] shrink-0 px-2 py-2 flex flex-col items-center justify-center text-center rounded-md">
          <FolderIcon className="h-5 w-5" aria-hidden />
          <span className="mt-1 text-sm font-medium">{count}</span>
        </div>
        <div>
          <p className="text-sm font-semibold text-primary">Hub</p>
          <p className="text-base font-semibold text-gray-900">{hub.title}</p>
          {programLine && <p className="mt-2 text-sm text-gray-600">{programLine}</p>}
        </div>
        <SaveMark align="end" />
      </div>
    );
  }
  if (optionId === 'modern-row') {
    return (
      <div className="flex w-full overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-primary/10 text-primary px-3 py-3 flex flex-col items-center justify-center text-center min-w-[5.5rem]">
          <FolderIcon className="h-5 w-5" aria-hidden />
          <span className="mt-1 text-sm font-medium">{count}</span>
        </div>
        <div className="min-w-0 flex-1 px-4 py-4 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-primary">Hub</p>
            <p className="text-base font-semibold text-gray-900">{hub.title}</p>
            {programLine && <p className="text-sm text-gray-600">{programLine}</p>}
          </div>
          <SaveMark align="end" />
        </div>
      </div>
    );
  }
  const card = (
    <ProposalCard className="p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-sm font-semibold text-primary">
            <FolderIcon className="h-5 w-5 flex-shrink-0" aria-hidden />
            Hub
          </p>
          <p className="mt-1 text-lg font-semibold text-gray-900">{hub.title}</p>
          {programLine && <p className="mt-1 text-sm text-gray-600">{programLine}</p>}
        </div>
        <SaveMark align="end" />
      </div>
      {about && <p className="mt-2 text-sm text-gray-600">{about}</p>}
      <p className="mt-4 text-sm text-gray-600">{count}</p>
      <div className="mt-4">
        <QuietAction>Open folder</QuietAction>
      </div>
    </ProposalCard>
  );
  return columnCard(frame, card);
}

function modernResource(post: WPPost, attachments: WPMedia[], optionId: string, frame: ReviewFrame) {
  const { title, date, under, excerpt } = postFacts(post, attachments);
  const clinical = resourceClinical(excerpt);
  const offer = resourceOffer(attachments);
  const row = optionId === 'modern-row';
  const card = (
    <ProposalCard className="p-4 flex flex-col">
      {!row && (
        <div className="order-last mt-4">
          <QuietAction label={`Open ${title}`}>Open</QuietAction>
        </div>
      )}
      <div className="flex items-start gap-3">
        <p className="min-w-0 flex-1 flex items-center gap-2 text-sm font-semibold text-primary">
          <DocumentTextIcon className="h-5 w-5 flex-shrink-0" aria-hidden />
          Resource
        </p>
        <SaveMark align="end" />
      </div>
      <p className={`${row ? 'text-base' : 'text-lg'} font-semibold text-gray-900`}>{title}</p>
      {!row && <p className="mt-1 text-sm text-gray-600">First author, Second author</p>}
      {!row && clinical && <p className="mt-2 text-sm text-gray-600">{clinical}</p>}
      <div className={row ? 'mt-2' : 'mt-4'}>
        {under && (
          <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
            <span className="h-2 w-2 rounded-sm bg-warning flex-shrink-0" aria-hidden />
            Under revision
          </p>
        )}
        <p className="text-sm text-gray-600">{offer ? `${date} · ${offer}` : date}</p>
      </div>
    </ProposalCard>
  );
  return optionId === 'modern-column' ? columnCard(frame, card) : card;
}

function modernEvent(event: AppEvent, optionId: string, frame: ReviewFrame) {
  const past = optionId === 'modern-past';
  const stacked = optionId === 'modern-column';
  const row = optionId === 'modern-row';
  const lines = [past ? '' : clockLine(event), event.venue ?? ''].filter(Boolean);
  const blurb = eventBlurb(event.excerpt);
  const title = eventTitle(event);
  const showCalendar = !row && !stacked;
  const action = past && event.recordingUrl ? (
    <QuietAction icon={<PlayIcon className="h-4 w-4" aria-hidden />}>Watch recording</QuietAction>
  ) : (
    <QuietAction>View event</QuietAction>
  );
  const day = (
    <div className="flex flex-col items-center flex-shrink-0">
      <CalendarDay event={event} past={past} large={!row && !stacked} />
      {past && <p className="mt-1 text-sm text-gray-600">{format(startOf(event), 'yyyy')}</p>}
    </div>
  );
  const copy = (
    <div className="min-w-0 flex-1">
      <div className={showCalendar ? 'relative' : undefined}>
        <p className={`${row ? 'text-base' : 'text-lg'} font-semibold text-gray-900`}>
          {showCalendar && <span className="float-right h-7 w-11" aria-hidden />}
          {title}
        </p>
        {showCalendar && (
          <div className="absolute top-0 right-0">
            <CalendarCorner />
          </div>
        )}
      </div>
      {blurb.topic && <p className={`mt-2 text-sm text-gray-600 ${row ? 'line-clamp-1' : 'line-clamp-2'}`}>{blurb.topic}</p>}
      {!row && blurb.presenter && <p className="mt-1 text-sm text-gray-600">{blurb.presenter}</p>}
      {lines.length > 0 && (
        <div className={row ? 'mt-1' : 'mt-2'}>
          {lines.map((line) => (
            <p key={line} className="text-sm text-gray-600">{line}</p>
          ))}
        </div>
      )}
      {!row && <div className="mt-4 flex justify-end">{action}</div>}
    </div>
  );
  const body = (
    <div className="flex items-start gap-4">
      {day}
      {copy}
    </div>
  );
  if (row) {
    return <div className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg">{body}</div>;
  }
  const card = <ProposalCard className="p-4">{body}</ProposalCard>;
  return stacked ? columnCard(frame, card) : card;
}

function CourseMeter({ progress, done }: { progress: number; done: boolean }) {
  const width = done ? 100 : Math.max(0, Math.min(100, progress));
  return (
    <div className="mt-4 h-2 rounded-full bg-primary/10 overflow-hidden" aria-hidden>
      <div className={`h-full ${done ? 'bg-[#3F6E60]' : 'bg-primary'}`} style={{ width: `${width}%` }} />
    </div>
  );
}

function courseStatus(stage: CourseStage) {
  if (stage === 'done') return { label: 'Completed', className: 'text-[#3F6E60]' };
  if (stage === 'active') return { label: 'In progress', className: 'text-primary' };
  return { label: 'Not enrolled', className: 'text-gray-600' };
}

function courseAction(stage: CourseStage, title: string) {
  if (stage === 'done') return <QuietAction label={`Review course ${title}`}>Review course</QuietAction>;
  if (stage === 'active') return <QuietAction label={`Continue ${title}`}>Continue</QuietAction>;
  return <QuietAction label={`View course ${title}`}>View course</QuietAction>;
}

function courseBlurb(course: Course) {
  const original = plain(course.description);
  let text = original;
  const title = plain(course.title);
  let changed = false;
  if (title && text.slice(0, title.length).toLowerCase() === title.toLowerCase()) {
    text = text.slice(title.length);
    changed = true;
  }
  if (/\s*elearning module\.?\s*$/i.test(text)) {
    text = text.replace(/\s*elearning module\.?\s*$/i, '');
    changed = true;
  }
  if (!changed) return original;
  return text.replace(/^[\s:–—\-.,]+/, '').replace(/[\s.]+$/, '').trim();
}

function courseFact(course: Course, stage: CourseStage, finished: number, progress: number) {
  const { time, line } = courseLength(course);
  const modules = course.modules?.length ?? 0;
  if (stage === 'active' && modules > 0) {
    const noun = modules === 1 ? 'module' : 'modules';
    const percent = Math.round(progress);
    const fromCount = Math.round((finished / modules) * 100);
    if (fromCount !== percent) return `${time} · ${percent}%`;
    return `${time} · ${finished} of ${modules} ${noun}`;
  }
  return line;
}

function modernCourse(course: Course, optionId: string, frame: ReviewFrame, stage: CourseStage, progress: number, finished: number) {
  const row = optionId === 'modern-row';
  const status = courseStatus(stage);
  const blurb = courseBlurb(course);
  const fact = courseFact(course, stage, finished, progress);
  if (row) {
    return (
      <div className="flex items-start gap-4 w-full px-4 py-3 bg-white border border-gray-200 rounded-lg">
        <AcademicCapIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold ${status.className}`}>{status.label}</p>
          <p className="text-base font-semibold text-gray-900">{course.title}</p>
          {blurb && <p className="mt-1 text-sm text-gray-600 line-clamp-1">{blurb}</p>}
          <p className="mt-1 text-sm text-gray-600">{fact}</p>
        </div>
      </div>
    );
  }
  const card = (
    <ProposalCard className="p-4">
      <p className="flex items-center gap-2 text-sm font-semibold text-primary">
        <AcademicCapIcon className="h-5 w-5 flex-shrink-0" aria-hidden />
        Course
      </p>
      <p className="mt-1 text-lg font-semibold text-gray-900">{course.title}</p>
      {blurb && <p className="mt-2 text-sm text-gray-600 line-clamp-2">{blurb}</p>}
      <p className={`mt-4 text-sm font-semibold ${status.className}`}>{status.label}</p>
      <p className="text-sm text-gray-600">{fact}</p>
      {stage !== 'open' && <CourseMeter progress={progress} done={stage === 'done'} />}
      <div className="mt-4 flex justify-end">{courseAction(stage, course.title)}</div>
    </ProposalCard>
  );
  if (optionId === 'modern-side') return <div className="max-w-xs">{card}</div>;
  if (optionId === 'modern-column') return columnCard(frame, card);
  return card;
}

export function programScent(program: Program, optionId: string) {
  if (optionId.startsWith('modern-')) return <ModernProgram program={program} optionId={optionId} />;
  if (optionId.startsWith('solid-')) return solidProgram(program, optionId);
  const count = hubWord(program.hubs.length);
  const hidden = Math.max(program.hubs.length - 3, 0);
  if (optionId === 'object-room-doors' || optionId === 'handoff-stack-doors') {
    return (
      <ProposalCard className="p-6">
        <div className="flex items-start gap-4">
          <Plate icon={<RectangleStackIcon className="h-6 w-6 text-primary" aria-hidden />} />
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{program.title}</h3>
            {optionId === 'handoff-stack-doors' && <p className="mt-1 text-sm text-gray-600">{count}</p>}
          </div>
        </div>
        <Doors hubs={program.hubs} />
      </ProposalCard>
    );
  }
  if (optionId === 'place-open-doors') {
    return (
      <ProposalCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900">{program.title}</h3>
        <Doors hubs={program.hubs} />
      </ProposalCard>
    );
  }
  if (optionId === 'object-room-count' || optionId === 'handoff-hub-tally') {
    return (
      <div className="flex items-center gap-3 min-h-[44px] p-4 bg-white border border-gray-200 rounded-lg">
        <RectangleStackIcon className="h-5 w-5 text-primary flex-shrink-0" aria-hidden />
        <span className="text-base font-semibold text-gray-900">{program.title}</span>
        <span className="text-sm text-gray-600 ml-auto">{count}</span>
      </div>
    );
  }
  if (optionId === 'place-program-first') {
    return (
      <div className="min-h-[44px] p-4 bg-white border border-gray-200 rounded-lg">
        <Kind>Program</Kind>
        <p className="text-base font-semibold text-gray-900">{program.title}</p>
        <p className="text-sm text-gray-600">{count}</p>
      </div>
    );
  }
  if (optionId === 'object-room-more' || optionId === 'place-rest-inside') {
    return (
      <ProposalCard className="p-6">
        {optionId === 'object-room-more' && (
          <Plate icon={<RectangleStackIcon className="h-6 w-6 text-primary" aria-hidden />} />
        )}
        <h3 className={`${optionId === 'object-room-more' ? 'mt-4' : ''} text-lg font-semibold text-gray-900`}>{program.title}</h3>
        <Doors hubs={program.hubs.slice(0, 3)} />
        {hidden > 0 && <p className="mt-2 text-sm text-gray-600">+ {hidden} more hubs</p>}
      </ProposalCard>
    );
  }
  return (
    <ProposalCard className="p-6">
      <div className="flex items-start gap-4">
        <Plate icon={<RectangleStackIcon className="h-6 w-6 text-primary" aria-hidden />} />
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{program.title}</h3>
          <p className="mt-1 text-base text-gray-600">{count}</p>
        </div>
      </div>
      <Doors hubs={program.hubs.slice(0, 3)} />
      <div className="mt-3 space-y-2">
        <div className="h-2 rounded bg-primary/10" />
        <div className="h-2 rounded bg-primary/10 ml-2" />
        <div className="h-2 rounded bg-primary/10 ml-4" />
      </div>
    </ProposalCard>
  );
}

export function hubScent(hub: Hub, optionId: string, frame: ReviewFrame, place?: 'search') {
  if (optionId.startsWith('modern-')) return modernHub(hub, optionId, frame, place);
  if (optionId.startsWith('solid-')) return solidHub(hub, optionId, frame);
  const full = fullness(hub);
  const resources = resourceWord(hub.resources.length);
  const program = hub.program?.title;
  const folder = <FolderIcon className="h-6 w-6 text-primary" aria-hidden />;
  const smallFolder = <FolderIcon className="h-5 w-5 text-primary flex-shrink-0" aria-hidden />;
  if (optionId === 'object-folder-counts' || optionId === 'object-folder-room' || optionId === 'object-folder-home') {
    return columnCard(
      frame,
      <ProposalCard className="p-6">
        <div className="flex items-start justify-between gap-3">
          <Plate icon={folder} />
          <SaveMark />
        </div>
        {program && <p className="mt-4 text-sm text-gray-600">{program}</p>}
        <h3 className="mt-1 text-lg font-semibold text-gray-900">{hub.title}</h3>
        <p className="mt-2 text-sm text-gray-600">{full}</p>
      </ProposalCard>
    );
  }
  if (optionId === 'place-full-folder' || optionId === 'place-named-room' || optionId === 'place-hub-invite') {
    const titleClass = optionId === 'place-hub-invite' ? 'text-xl' : 'text-lg';
    return columnCard(
      frame,
      <ProposalCard className="relative p-6">
        <div className="absolute top-2 right-2">
          <SaveMark />
        </div>
        {optionId === 'place-named-room' && program && <p className="text-sm text-gray-600 pr-12">{program}</p>}
        <h3 className={`${titleClass} font-semibold text-gray-900 pr-12`}>{hub.title}</h3>
        <p className="mt-2 text-sm text-gray-600">{full}</p>
      </ProposalCard>
    );
  }
  if (optionId === 'handoff-folder-mark' || optionId === 'handoff-room-mark') {
    return columnCard(
      frame,
      <ProposalCard className="p-6">
        <div className="flex items-start justify-between gap-3">
          <Plate icon={folder} />
          <SaveMark />
        </div>
        {optionId === 'handoff-room-mark' && program && <p className="mt-4 text-sm font-medium text-primary">{program}</p>}
        <h3 className="mt-1 text-lg font-semibold text-gray-900">{hub.title}</h3>
        <p className="mt-2 text-sm text-gray-600">{resources}</p>
      </ProposalCard>
    );
  }
  if (optionId === 'handoff-center-folder') {
    return columnCard(
      frame,
      <ProposalCard className="p-6 text-center">
        <FolderIcon className="h-12 w-12 text-primary mx-auto" aria-hidden />
        <h3 className="mt-4 text-xl font-semibold text-gray-900">{hub.title}</h3>
        <p className="mt-2 text-sm text-gray-600">{resources}</p>
      </ProposalCard>
    );
  }
  if (optionId === 'object-folder-tile') {
    return columnCard(
      frame,
      <ProposalCard className="p-4">
        <div className="flex items-start justify-between gap-2">
          {smallFolder}
          <SaveMark />
        </div>
        <p className="mt-2 text-sm font-medium text-gray-900">{hub.title}</p>
        <p className="mt-1 text-sm text-gray-600">{full}</p>
      </ProposalCard>
    );
  }
  if (optionId === 'handoff-folder-tile') {
    return columnCard(
      frame,
      <ProposalCard className="flex items-center gap-2 min-h-[44px] p-4">
        {smallFolder}
        <span className="text-sm font-medium text-gray-900">{hub.title}</span>
      </ProposalCard>
    );
  }
  if (optionId === 'place-your-hub') {
    return (
      <div className="flex items-center justify-between gap-3 min-h-[44px] px-4 py-2 bg-white border border-gray-200 rounded-lg">
        <span className="text-sm text-gray-900">{hub.title}</span>
        <span className="text-sm text-gray-600">{full}</span>
        <SaveMark />
      </div>
    );
  }
  if (optionId === 'object-folder-chip') {
    return (
      <span className="inline-flex items-center gap-2 min-h-[44px] px-4 bg-white border border-gray-200 rounded-lg shadow-sm">
        {smallFolder}
        <span className="text-sm font-medium text-gray-900">{hub.title}</span>
        {program && <span className="text-sm text-gray-600 truncate">{program}</span>}
        <span className="text-sm text-gray-600">{resources}</span>
        <SaveMark />
      </span>
    );
  }
  if (optionId === 'place-room-chip') {
    return (
      <span className="inline-flex items-center gap-2 min-h-[44px] px-4 bg-white border border-gray-200 rounded-lg shadow-sm">
        <span className="text-sm text-gray-900">{program ? `${program} · ${hub.title}` : hub.title}</span>
        <SaveMark />
      </span>
    );
  }
  if (optionId === 'handoff-room-chip') {
    return (
      <span className="inline-flex items-center gap-2 min-h-[44px] px-4 bg-white border border-gray-200 rounded-lg shadow-sm">
        {smallFolder}
        {program && <span className="text-sm text-gray-600">{program}</span>}
        <span className="text-sm font-medium text-gray-900">{hub.title}</span>
      </span>
    );
  }
  if (optionId === 'object-folder-row' || optionId === 'handoff-folder-count') {
    return (
      <div className="flex items-center gap-3 min-h-[44px] p-4 bg-white border border-gray-200 rounded-lg">
        {smallFolder}
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-gray-900">{hub.title}</p>
          {optionId === 'object-folder-row' && program && <p className="text-sm text-gray-600">{program}</p>}
        </div>
        <span className="text-sm text-gray-600">{resources}</span>
        {optionId === 'object-folder-row' && <SaveMark />}
      </div>
    );
  }
  return (
    <div className="min-h-[44px] p-4 bg-white border border-gray-200 rounded-lg">
      <Kind>Hub</Kind>
      <p className="text-base font-semibold text-gray-900">{hub.title}</p>
      <p className="text-sm text-gray-600">{resources}</p>
    </div>
  );
}

function DocBody({
  post,
  attachments,
  showIcon,
  showChips,
  showCount,
  showDate,
  showSave,
  titleClass,
}: {
  post: WPPost;
  attachments: WPMedia[];
  showIcon?: boolean;
  showChips?: boolean;
  showCount?: boolean;
  showDate?: boolean;
  showSave?: boolean;
  titleClass: string;
}) {
  const { title, date, under, files } = postFacts(post, attachments);
  return (
    <ProposalCard className="p-4">
      <div className="flex items-start gap-4">
        {showIcon && <Plate icon={<DocumentTextIcon className="h-6 w-6 text-primary" aria-hidden />} />}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <h3 className={`${titleClass} font-semibold text-gray-900`}>{title}</h3>
            {showSave && <SaveMark />}
          </div>
          {(showDate || showCount) && (
            <p className="mt-2">
              <FactLine date={showDate ? date : ''} under={under} files={showCount ? files : undefined} />
            </p>
          )}
          {!showDate && under && !showCount && (
            <p className="mt-2">
              <Revision />
            </p>
          )}
        </div>
      </div>
      {showChips && (
        <div className="mt-4 border-t border-gray-200 pt-4">
          <AttachmentChips attachments={attachments} />
        </div>
      )}
    </ProposalCard>
  );
}

export function resourceScent(post: WPPost, attachments: WPMedia[], optionId: string, frame: ReviewFrame) {
  if (optionId.startsWith('modern-')) return modernResource(post, attachments, optionId, frame);
  if (optionId.startsWith('solid-')) return solidResource(post, attachments, optionId, frame);
  const { title, date, under, files } = postFacts(post, attachments);
  const narrow = (node: ReactNode) => columnCard(frame, node);
  if (optionId === 'object-doc-chips' || optionId === 'object-doc-column') {
    return optionId === 'object-doc-column'
      ? narrow(<DocBody post={post} attachments={attachments} showIcon showChips showDate showSave titleClass="text-lg" />)
      : <DocBody post={post} attachments={attachments} showIcon showChips showDate showSave titleClass="text-lg" />;
  }
  if (optionId === 'object-doc-facts' || optionId === 'object-doc-home' || optionId === 'object-doc-date') {
    const node = <DocBody post={post} attachments={attachments} showIcon showDate showCount showSave={optionId === 'object-doc-facts'} titleClass="text-lg" />;
    return optionId === 'object-doc-facts' ? node : narrow(node);
  }
  if (optionId === 'place-shelf-line' || optionId === 'place-found-state') {
    return (
      <ProposalCard className="p-4">
        <h3 className={`${optionId === 'place-found-state' ? 'text-base' : 'text-lg'} font-semibold text-gray-900`}>{title}</h3>
        <div className="mt-2 flex items-center justify-between gap-3">
          <FactLine date={date} under={under} files={files} />
          <SaveMark />
        </div>
      </ProposalCard>
    );
  }
  if (optionId === 'place-file-face' || optionId === 'handoff-sheet-chips') {
    return narrow(
      <ProposalCard className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <SaveMark />
        </div>
        <p className="mt-2">
          <FactLine date={date} under={under} />
        </p>
        <div className="mt-4 border-t border-gray-200 pt-4">
          <AttachmentChips attachments={attachments} />
        </div>
      </ProposalCard>
    );
  }
  if (optionId === 'handoff-page-mark' || optionId === 'handoff-fact-line' || optionId === 'handoff-home-status' || optionId === 'handoff-related-status') {
    const node = (
      <DocBody
        post={post}
        attachments={attachments}
        showIcon={optionId === 'handoff-page-mark'}
        showDate
        showCount
        showSave={optionId === 'handoff-page-mark' || optionId === 'handoff-fact-line'}
        titleClass="text-lg"
      />
    );
    return optionId === 'handoff-home-status' || optionId === 'handoff-related-status' ? narrow(node) : node;
  }
  if (optionId === 'object-doc-scan') {
    return (
      <ProposalCard className="flex items-start gap-3 p-4">
        <DocumentTextIcon className="h-5 w-5 text-primary flex-shrink-0 mt-1" aria-hidden />
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <p className="mt-1">
            <FactLine date={date} under={under} files={files} />
          </p>
        </div>
        <SaveMark />
      </ProposalCard>
    );
  }
  if (optionId === 'place-scan-ledger') {
    return (
      <ProposalCard className="flex items-start justify-between gap-4 min-h-[44px] p-4">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 text-right">
          <FactLine date={date} under={under} files={files} />
        </p>
      </ProposalCard>
    );
  }
  if (optionId === 'handoff-scan-count') {
    return (
      <ProposalCard className="flex items-center justify-between gap-4 min-h-[44px] px-4 py-2">
        <span className="text-base font-semibold text-gray-900">{title}</span>
        <span className="text-sm text-gray-600">
          {under && (
            <>
              <Revision />
              {' · '}
            </>
          )}
          {files}
        </span>
        <SaveMark />
      </ProposalCard>
    );
  }
  if (optionId === 'place-neighbor-date' || optionId === 'place-doc-invite') {
    return narrow(
      <ProposalCard className={optionId === 'place-doc-invite' ? 'p-6' : 'p-4'}>
        <h3 className={`${optionId === 'place-doc-invite' ? 'text-xl' : 'text-lg'} font-semibold text-gray-900`}>{title}</h3>
        <p className="mt-2">
          <FactLine date={date} under={under} />
        </p>
      </ProposalCard>
    );
  }
  if (optionId === 'object-doc-row') {
    return (
      <div className="flex items-center gap-3 min-h-[44px] px-4 py-2 bg-white border border-gray-200 rounded-lg">
        <DocumentTextIcon className="h-5 w-5 text-primary flex-shrink-0" aria-hidden />
        <span className="text-sm text-gray-900">{title}</span>
        <span className="ml-auto text-sm text-gray-600 text-right">
          <FactLine date={date} under={under} files={files} />
        </span>
      </div>
    );
  }
  if (optionId === 'place-your-doc') {
    return (
      <div className="flex items-center justify-between gap-4 min-h-[44px] px-4 py-2 bg-white border border-gray-200 rounded-lg">
        <span className="text-sm text-gray-900">{title}</span>
        <FactLine date={date} under={under} />
      </div>
    );
  }
  if (optionId === 'handoff-revision-row') {
    return (
      <div className="flex items-center justify-between gap-4 min-h-[44px] px-4 py-2 bg-white border border-gray-200 rounded-lg">
        <span className="text-sm text-gray-900">{title}</span>
        <span className="text-sm text-gray-600">
          {under && (
            <>
              <Revision />
              {' · '}
            </>
          )}
          {files}
        </span>
      </div>
    );
  }
  if (optionId === 'object-doc-search') {
    return (
      <div className="flex items-center gap-3 min-h-[44px] p-4 bg-white border border-gray-200 rounded-lg">
        <DocumentTextIcon className="h-5 w-5 text-primary flex-shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-gray-900">{title}</p>
          <FactLine date={date} under={under} files={files} />
        </div>
        <SaveMark />
      </div>
    );
  }
  if (optionId === 'place-resource-first') {
    return (
      <div className="min-h-[44px] p-4 bg-white border border-gray-200 rounded-lg">
        <Kind>Resource</Kind>
        <p className="text-base font-semibold text-gray-900">{title}</p>
        <FactLine date={date} under={under} />
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 min-h-[44px] p-4 bg-white border border-gray-200 rounded-lg">
      <DocumentTextIcon className="h-5 w-5 text-gray-400 flex-shrink-0" aria-hidden />
      <div className="min-w-0">
        <p className="text-base font-semibold text-gray-900">{title}</p>
        <span className="text-sm text-gray-600">
          {under && (
            <>
              <Revision />
              {' · '}
            </>
          )}
          {files}
        </span>
      </div>
    </div>
  );
}

export function eventScent(event: AppEvent, optionId: string, frame: ReviewFrame) {
  if (optionId.startsWith('modern-')) return modernEvent(event, optionId, frame);
  if (optionId.startsWith('solid-')) return solidEvent(event, optionId, frame);
  const narrow = (node: ReactNode) => columnCard(frame, node);
  const venue = event.venue;
  if (optionId === 'object-day-next') {
    return (
      <ProposalCard>
        <div className="flex gap-4 p-6">
          <DayBadge event={event} tone="primary" size="lg" dayClass="text-2xl" />
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
            <p className="mt-2 text-sm text-gray-600">{clockLine(event)}</p>
            {venue && <p className="mt-1 text-sm text-gray-600">{venue}</p>}
          </div>
        </div>
        <div className="flex justify-end border-t border-gray-200 px-6 py-3">
          <CalendarMark />
        </div>
      </ProposalCard>
    );
  }
  if (optionId === 'place-day-venue') {
    return (
      <ProposalCard className="p-6">
        <div className="flex gap-4">
          <DayBadge event={event} tone="primary" size="lg" dayClass="text-2xl" />
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
            <p className="mt-2 text-sm text-gray-600">{clockLine(event)}</p>
            {venue && <p className="mt-1 text-sm text-gray-600">{venue}</p>}
            <div className="mt-4">
              <CalendarMark />
            </div>
          </div>
        </div>
      </ProposalCard>
    );
  }
  if (optionId === 'handoff-day-calendar') {
    return (
      <ProposalCard className="p-6">
        <div className="flex gap-4">
          <DayBadge event={event} tone="primary" size="lg" dayClass="text-2xl" />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-4">
              <h3 className="text-xl font-semibold text-gray-900">{event.title}</h3>
              <CalendarMark />
            </div>
            <p className="mt-2 text-sm text-gray-600">{clockLine(event)}</p>
            {venue && <p className="mt-1 text-sm text-gray-600">{venue}</p>}
          </div>
        </div>
      </ProposalCard>
    );
  }
  if (optionId === 'object-day-past') {
    return (
      <ProposalCard className="p-6">
        <div className="flex gap-4">
          <DayBadge event={event} tone="neutral" size="lg" dayClass="text-2xl" />
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
            {venue && <p className="mt-2 text-sm text-gray-600">{venue}</p>}
            <div className="mt-4 flex flex-col items-start gap-2">
              {event.recordingUrl && <WatchMark />}
              <CalendarMark />
            </div>
          </div>
        </div>
      </ProposalCard>
    );
  }
  if (optionId === 'place-played-day') {
    return (
      <ProposalCard className="p-6">
        <div className="flex gap-4">
          <DayBadge event={event} tone="neutral" size="lg" dayClass="text-2xl" />
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
            <p className="mt-2 text-sm text-gray-600">{format(startOf(event), 'MMMM d, yyyy')}</p>
            {venue && <p className="mt-1 text-sm text-gray-600">{venue}</p>}
            <div className="mt-4 flex flex-wrap gap-4">
              {event.recordingUrl && <WatchMark />}
              <CalendarMark />
            </div>
          </div>
        </div>
      </ProposalCard>
    );
  }
  if (optionId === 'handoff-watch-day') {
    return (
      <ProposalCard className="p-6">
        <div className="flex gap-4">
          <DayBadge event={event} tone="neutral" size="lg" dayClass="text-2xl" />
          <div className="min-w-0 flex-1">
            <h3 className="text-xl font-semibold text-gray-900">{event.title}</h3>
            <p className="mt-2 text-sm text-gray-600">{format(startOf(event), 'MMMM d, yyyy')}</p>
            <div className="mt-4 flex flex-wrap gap-4">
              {event.recordingUrl && <WatchMark />}
              <CalendarMark />
            </div>
          </div>
        </div>
      </ProposalCard>
    );
  }
  if (optionId === 'object-day-home' || optionId === 'place-day-invite' || optionId === 'handoff-day-plate') {
    return narrow(
      <ProposalCard className="p-4">
        <DayBadge event={event} tone="primary" size="lg" dayClass="text-2xl" />
        <h3 className={`mt-4 ${optionId === 'place-day-invite' ? 'text-xl' : 'text-lg'} font-semibold text-gray-900`}>{event.title}</h3>
        {optionId === 'object-day-home' && <p className="mt-2 text-sm text-gray-600">{clockLine(event)}</p>}
        {venue && <p className="mt-1 text-sm text-gray-600">{venue}</p>}
      </ProposalCard>
    );
  }
  if (optionId === 'object-day-row') {
    return (
      <div className="flex items-center gap-4 min-h-[44px] px-4 py-2 bg-white border border-gray-200 rounded-lg">
        <DayBadge event={event} tone="primary" size="md" dayClass="text-xl" />
        <span className="text-sm text-gray-900">{event.title}</span>
        {venue && <span className="ml-auto text-sm text-gray-600">{venue}</span>}
      </div>
    );
  }
  if (optionId === 'place-your-event') {
    return (
      <div className="flex items-center justify-between gap-4 min-h-[44px] px-4 py-2 bg-white border border-gray-200 rounded-lg">
        <span className="text-sm text-gray-900">{event.title}</span>
        <span className="text-sm text-gray-600 flex-shrink-0">
          {format(startOf(event), 'MMM d')}
          {venue ? ` · ${venue}` : ''}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-4 min-h-[44px] px-4 py-2 bg-white border border-gray-200 rounded-lg">
      <span className="text-sm font-medium text-primary flex-shrink-0">{format(startOf(event), 'MMM d')}</span>
      <span className="text-sm text-gray-900">{event.title}</span>
    </div>
  );
}

export function courseScent(course: Course, optionId: string, frame: ReviewFrame, stage: CourseStage, progress = 0, finished = 0) {
  if (optionId.startsWith('modern-')) return modernCourse(course, optionId, frame, stage, progress, finished);
  if (optionId.startsWith('solid-')) return solidCourse(course, optionId, frame, stage, finished);
  const { time, moduleLabel, line } = courseLength(course);
  const cap = <AcademicCapIcon className="h-6 w-6 text-primary" aria-hidden />;
  const smallCap = <AcademicCapIcon className="h-5 w-5 text-primary flex-shrink-0" aria-hidden />;
  const yours = stage === 'open' ? null : (
    <span className={`text-sm font-medium ${stage === 'done' ? 'text-accent' : 'text-primary'}`}>
      {stage === 'done' ? 'Completed' : 'In progress'}
    </span>
  );
  if (optionId === 'object-class-grid') {
    return columnCard(
      frame,
      <ProposalCard className="p-4">
        <Plate icon={cap} />
        {yours && <div className="mt-4">{yours}</div>}
        <h3 className="mt-2 text-lg font-semibold text-gray-900">{course.title}</h3>
        <p className="mt-2 text-sm text-gray-600">{line}</p>
      </ProposalCard>
    );
  }
  if (optionId === 'place-completed-face' || optionId === 'handoff-yours-grid') {
    return columnCard(
      frame,
      <ProposalCard className="p-4">
        {yours || <p className="text-sm text-gray-600">{time}</p>}
        <h3 className="mt-2 text-lg font-semibold text-gray-900">{course.title}</h3>
        {optionId === 'handoff-yours-grid' && <p className="mt-2 text-sm text-gray-600">{time}</p>}
      </ProposalCard>
    );
  }
  if (optionId === 'object-class-list') {
    return (
      <ProposalCard className="flex items-center gap-4 min-h-[44px] px-4 py-3">
        {smallCap}
        <span className="text-lg font-semibold text-gray-900">{course.title}</span>
        <span className="text-sm text-gray-600">{time}</span>
        <span className="text-sm text-gray-600">{moduleLabel}</span>
        <span className="ml-auto">{yours}</span>
      </ProposalCard>
    );
  }
  if (optionId === 'place-state-at-end') {
    return (
      <ProposalCard className="flex items-center justify-between gap-4 min-h-[44px] px-4 py-3">
        <span className="text-lg font-semibold text-gray-900">{course.title}</span>
        {yours || <span className="text-sm text-gray-600">{time}</span>}
      </ProposalCard>
    );
  }
  if (optionId === 'handoff-yours-list') {
    return (
      <ProposalCard className="flex items-center justify-between gap-4 min-h-[44px] px-4 py-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{course.title}</h3>
          <p className="text-sm text-gray-600">{time}</p>
        </div>
        {yours}
      </ProposalCard>
    );
  }
  if (optionId === 'object-class-step' || optionId === 'place-your-course' || optionId === 'handoff-view-row') {
    return (
      <div className="flex items-center gap-4 min-h-[44px] px-4 py-2 bg-white border border-gray-200 rounded-lg">
        {optionId === 'object-class-step' && smallCap}
        <span className="text-sm text-gray-900">{course.title}</span>
        <span className="ml-auto inline-flex items-center gap-4">
          {yours}
          <ViewCourseMark />
        </span>
      </div>
    );
  }
  return (
    <div className="max-w-xs min-h-[44px] px-2 py-2">
      <div className="flex items-start gap-2">
        {optionId === 'object-class-side' && smallCap}
        <p className="text-sm text-gray-700">{course.title}</p>
      </div>
      {yours && <div className="mt-1">{yours}</div>}
      {(optionId === 'object-class-side' || optionId === 'place-sidebar-step' || optionId === 'handoff-view-narrow') && <ViewCourseMark />}
    </div>
  );
}
